
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import MainNavigation from './MainNavigation'
import { isBusinessTier } from 'lib/utils/billing'
import AppNotifications from './AppNotifications'
import WarehouseSetupApp from '../pages/workspace-warehouses/containers/warehouse-setup-app'
import Loader from './Loader'
import Lockout from './Lockout'
import ShowHide from './ShowHide'
import Environment from './Environment'
import {
  checkFeatureFlag,
  setFeatureFlag,
  toggleFeatureEditor,
  fetchBillingCountsForPastXMonths,
  fetchMTUsForCurrentPeriod,
  fetchBillingByWorkspaceSlugIfNeeded,
  fetchAllMetadatas,
  fetchLockoutByWorkspaceSlug,
  showWarehouseSetup,
  fetchSources,
  fetchWarehouses,
  setAppSettings
} from 'actions'
import {
  getActiveWorkspaceSlug,
  getCurrentUser,
  getWorkspace,
  getBilling,
  hasBillingRequested,
  hasLockoutRequested,
  shouldLockoutRedirect,
  getLockoutByWorkspaceSlug,
  shouldShowFeature,
  getAllFeatures,
  isShowingFeatureEditor,
  getBillingCountForCurrentPeriod,
  getBillingCountForPastXMonths,
  isUserAPartner,
  getFirstMetadataByOwnerId,
  getSetupSettings,
  areSourcesAvailable,
  areWarehousesAvailable,
  getAppSettings
} from 'selectors'
import { navigationClicked } from 'analytics'
import FeaturesEditor from './FeaturesEditor'
import './App.css'

/**
 * State -> props.
 */

function mapStateToProps (state) {
  const { currentUser } = state
  const slug = getActiveWorkspaceSlug(state)
  const workspace = (getWorkspace(state, slug) || {})
  const user = getCurrentUser(state) || {}
  const billing = getBilling(state, slug)
  const isBillingLoaded = hasBillingRequested(state, slug)
  const lockout = getLockoutByWorkspaceSlug(state, slug)
  const isLockoutLoaded = (
    hasLockoutRequested(state, slug) && !shouldLockoutRedirect(state, slug, window.location)
  )
  const features = getAllFeatures(state)
  const lastMonthCount = getBillingCountForPastXMonths(state, slug, 1)
  const thisMonthCount = getBillingCountForCurrentPeriod(state, slug)
  const isPartner = isUserAPartner(state, user.id)
  const ownedMetadata = getFirstMetadataByOwnerId(state, user.id)
  const setupSettings = getSetupSettings(state)
  const sourcesAvailable = areSourcesAvailable(state, slug)
  const warehousesAvailable = areWarehousesAvailable(state, slug)
  const shouldShowWarehouseSetup =
    !setupSettings.isHidden && setupSettings.type === 'warehouse'
  const {
    shouldShowSourceRestriction,
    shouldShowWarehouseRestriction
  } = getAppSettings(state)

  return {
    lockout,
    isLockoutLoaded,
    workspace,
    user,
    billing,
    isBillingLoaded,
    features,
    ownedMetadata,
    sourcesAvailable,
    warehousesAvailable,
    isShowingFeatureEditor: isShowingFeatureEditor(state),
    shouldShowEnvToggle: user.isAdmin,
    shouldShowFeatureEditor: user.isAdmin,
    shouldShowAppNotifications: shouldShowFeature(state, 'app_notifications'),
    shouldShowWorkspaceLockout: shouldShowFeature(state, 'workspace_lockouts'),
    shouldShowActivityFeed: isBusinessTier({ billing }),
    shouldShowAudiences: shouldShowFeature(state, 'audiences'),
    shouldShowSimulator: shouldShowFeature(state, 'app_simulator_beta'),
    shouldShowRules: shouldShowFeature(state, 'app_event_rules'),
    shouldShowWorkspaceNotifications: shouldShowFeature(state, 'app_workspace_notifications') && currentUser.isAdmin,
    shouldShowPartnersLink: user.isAdmin || isPartner,
    shouldShowWarehouseSetup,
    shouldShowCustomers: shouldShowFeature(state, 'app_customers'),
    billingCount: {
      thisMonth: thisMonthCount,
      lastMonth: lastMonthCount
    },
    shouldShowSourceRestriction,
    shouldShowWarehouseRestriction
  }
}

/**
 * Dispatch -> props.
 */

function mapDispatchToProps (dispatch) {
  return {
    checkFeatures: () => {
      dispatch(checkFeatureFlag('app_notifications'))
      dispatch(checkFeatureFlag('workspace_lockouts'))
      dispatch(checkFeatureFlag('audiences'))
      dispatch(checkFeatureFlag('app_simulator_beta'))
      dispatch(checkFeatureFlag('app_event_rules'))
      dispatch(checkFeatureFlag('app_workspace_notifications'))
      dispatch(checkFeatureFlag('app_customers'))
    },
    setFeatureFlag: (id, value) => dispatch(setFeatureFlag(id, value)),
    toggleFeatureEditor: () => dispatch(toggleFeatureEditor()),
    fetchBillingByWorkspaceSlugIfNeeded: (slug) => dispatch(fetchBillingByWorkspaceSlugIfNeeded(slug)),
    fetchBillingCountsForPastXMonths: (slug) => dispatch(fetchBillingCountsForPastXMonths(slug, 1)),
    fetchMTUsForCurrentPeriod: (slug) => dispatch(fetchMTUsForCurrentPeriod(slug)),
    fetchLockoutByWorkspaceSlug: (slug) => dispatch(fetchLockoutByWorkspaceSlug(slug)),
    fetchAllMetadatas: () => dispatch(fetchAllMetadatas()),
    showWarehouseSetup: () => dispatch(showWarehouseSetup()),
    fetchSources: (slug) => dispatch(fetchSources(slug)),
    fetchWarehouses: (slug) => dispatch(fetchWarehouses(slug)),
    showSourceRestriction: (settings) => dispatch(setAppSettings({
      shouldShowSourceRestriction: true
    })),
    showWarehouseRestriction: (settings) => dispatch(setAppSettings({
      shouldShowWarehouseRestriction: true
    })),
    hideRestrictions: (settings) => dispatch(setAppSettings({
      shouldShowSourceRestriction: false,
      shouldShowWarehouseRestriction: false
    }))
  }
}

class App extends Component {
  render () {
    const {
      billing,
      billingCount,
      children,
      currentPage,
      features,
      hideRestrictions,
      isBillingLoaded,
      isLockoutLoaded,
      isShowingFeatureEditor,
      lockout,
      ownedMetadata,
      setFeatureFlag,
      shouldShowActivityFeed,
      shouldShowAppNotifications,
      shouldShowAudiences,
      shouldShowEnvToggle,
      shouldShowFeatureEditor,
      shouldShowPartnersLink,
      shouldShowRules,
      shouldShowSimulator,
      shouldShowSourceRestriction,
      shouldShowWarehouseRestriction,
      shouldShowWarehouseSetup,
      shouldShowWorkspaceLockout,
      shouldShowWorkspaceNotifications,
      showSourceRestriction,
      showWarehouseRestriction,
      showWarehouseSetup,
      shouldShowCustomers,
      sourcesAvailable,
      toggleFeatureEditor,
      user,
      warehousesAvailable,
      workspace
    } = this.props

    let partnersLink = ''
    if (user.isAdmin) {
      partnersLink = '/partner-portal/integration'
    } else if (ownedMetadata) {
      partnersLink = `/partner-portal/${ownedMetadata.type}/${ownedMetadata.metadata.slug}/metrics`
    }

    // TODO(tim): Move the following to redux state
    // get env vars.  favor `window.segment` over `process.env`
    const environment = window.segment.APP_ENV || process.env.APP_ENV || ''
    const version = window.segment.APP_VERSION || process.env.APP_VERSION || ''

    const isLockedOut = shouldShowWorkspaceLockout && lockout

    const onNavigationAddLinkClick = (link) => {
      if (link === 'source') {
        if (!sourcesAvailable) {
          showSourceRestriction()
        }
      } else if (link === 'warehouse') {
        if (warehousesAvailable) {
          showWarehouseSetup()
        } else {
          showWarehouseRestriction()
        }
      }

      navigationClicked(workspace, `add_${link}`)
    }

    return (isBillingLoaded && isLockoutLoaded) ? (
      <div className='App'>
        <ShowHide show={shouldShowAppNotifications && !isLockedOut}>
          <AppNotifications workspace={workspace} billing={billing} billingCount={billingCount} user={user} />
        </ShowHide>
        <MainNavigation
          workspace={workspace}
          active={currentPage}
          user={user}
          isLockedOut={isLockedOut}
          sourcesAvailable={sourcesAvailable}
          shouldShowSourceRestriction={shouldShowSourceRestriction}
          shouldShowWarehouseRestriction={shouldShowWarehouseRestriction}
          partnersLink={partnersLink}
          shouldShowPartnersLink={shouldShowPartnersLink}
          shouldShowActivityFeed={shouldShowActivityFeed}
          shouldShowAudiences={shouldShowAudiences}
          shouldShowSimulator={shouldShowSimulator}
          shouldShowRules={shouldShowRules}
          shouldShowWorkspaceNotifications={shouldShowWorkspaceNotifications}
          shouldShowCustomers={shouldShowCustomers}
          onAddButtonClick={() => navigationClicked(workspace, 'add_button')}
          onAddLinkClick={onNavigationAddLinkClick}
          onRestrictionClose={hideRestrictions} />
        <div className='App-page'>
          {children}
        </div>
        <ShowHide show={shouldShowEnvToggle}>
          <Environment environment={environment} version={version} />
        </ShowHide>
        <ShowHide show={shouldShowFeatureEditor}>
          <FeaturesEditor
            isShowingFeatureEditor={isShowingFeatureEditor}
            toggleFeatureEditor={toggleFeatureEditor}
            features={features}
            setFeatureFlag={setFeatureFlag} />
        </ShowHide>
        <ShowHide show={isLockedOut}>
          <Lockout workspaceSlug={workspace.slug} />
        </ShowHide>

        <WarehouseSetupApp
          workspaceSlug={workspace.slug}
          isShowing={shouldShowWarehouseSetup} />
      </div>
    ) : (
      <div className='App-loader'>
        <Loader isLoading />
      </div>
    )
  }

  componentDidMount () {
    const {
      checkFeatures,
      fetchAllMetadatas,
      fetchBillingByWorkspaceSlugIfNeeded,
      fetchBillingCountsForPastXMonths,
      fetchLockoutByWorkspaceSlug,
      fetchMTUsForCurrentPeriod,
      fetchSources,
      fetchWarehouses,
      workspace
    } = this.props

    if (workspace && workspace.slug) {
      fetchBillingByWorkspaceSlugIfNeeded(workspace.slug)
      fetchMTUsForCurrentPeriod(workspace.slug)
      fetchBillingCountsForPastXMonths(workspace.slug)
      fetchSources(workspace.slug)
      fetchWarehouses(workspace.slug)

      fetchLockoutByWorkspaceSlug(workspace.slug)
        .then(({ payload }) => {
          if (!payload.response) {
            return
          }

          // If not on post-redirect page (settings), redirect given lockout
          const redirectPath = `/${workspace.slug}/settings`
          if (window.location.pathname !== redirectPath) {
            window.location.href = redirectPath
          }
        })
    }
    fetchAllMetadatas()
    checkFeatures()
  }
}

App.defaultProps = {
  user: {}
}

App.propTypes = {
  billing: PropTypes.object,
  billingCount: PropTypes.object,
  checkFeatures: PropTypes.func.isRequired,
  children: PropTypes.any.isRequired,
  currentPage: PropTypes.string.isRequired,
  features: PropTypes.array,
  fetchAllMetadatas: PropTypes.func.isRequired,
  fetchBillingByWorkspaceSlugIfNeeded: PropTypes.func.isRequired,
  fetchBillingCountsForPastXMonths: PropTypes.func.isRequired,
  fetchLockoutByWorkspaceSlug: PropTypes.func.isRequired,
  fetchMTUsForCurrentPeriod: PropTypes.func.isRequired,
  fetchSources: PropTypes.func.isRequired,
  fetchWarehouses: PropTypes.func.isRequired,
  hideRestrictions: PropTypes.func.isRequired,
  isBillingLoaded: PropTypes.bool.isRequired,
  isLockoutLoaded: PropTypes.bool.isRequired,
  isShowingFeatureEditor: PropTypes.bool.isRequired,
  lockout: PropTypes.object,
  ownedMetadata: PropTypes.object,
  setFeatureFlag: PropTypes.func.isRequired,
  shouldShowActivityFeed: PropTypes.bool,
  shouldShowAppNotifications: PropTypes.bool,
  shouldShowAudiences: PropTypes.bool,
  shouldShowEnvToggle: PropTypes.bool,
  shouldShowFeatureEditor: PropTypes.bool,
  shouldShowPartnersLink: PropTypes.bool,
  shouldShowRules: PropTypes.bool,
  shouldShowSimulator: PropTypes.bool,
  shouldShowSourceRestriction: PropTypes.bool.isRequired,
  shouldShowWarehouseRestriction: PropTypes.bool.isRequired,
  shouldShowWarehouseSetup: PropTypes.bool,
  shouldShowWorkspaceLockout: PropTypes.bool,
  showSourceRestriction: PropTypes.func.isRequired,
  showWarehouseRestriction: PropTypes.func.isRequired,
  showWarehouseSetup: PropTypes.func.isRequired,
  sourcesAvailable: PropTypes.bool.isRequired,
  toggleFeatureEditor: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  warehousesAvailable: PropTypes.bool.isRequired,
  workspace: PropTypes.object.isRequired
}

App.displayName = 'App'

export default connect(mapStateToProps, mapDispatchToProps)(App)



// WEBPACK FOOTER //
// ./ui/App.js